import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from '@pkg/assets/icons/v2/plus.svg';
import { Modal } from '@pkg/components/Modal';
import { DepNftAsset } from '@pkg/components/deprecated';
import { type CombinedTokenData, useAppDispatch, useAppSelector, useMoveToWishlist, useWishlistData } from '@pkg/hooks';
import { getPagesContent } from '@pkg/hooks/deprecated';
import { useMediaQuery } from '@pkg/providers';

import { type CollectionParsedMetadata } from '@app/client/common/structs/marketplace';
import { wishlistActions } from '@app/store/slices';

import { Typography } from '../../DesignSystem';

import { CreateWishlist } from './CreateWishlist';
import { WishlistItemCard } from './WishlistItemCard';

import {
    AddIconCircle,
    CreateText,
    CreateWrapper,
    Footer,
    ModalLayout,
    NFTAssetWrapper,
    SectionWrapper,
    StyledStar,
    SubheadingLabel,
} from './styles';

export interface AddToWishListModalProps {
    assetData?: CombinedTokenData;
    collectionData?: CollectionParsedMetadata;
    slug?: string;
}

type WishlistStep = 'choose' | 'create';

export const AddToWishlistModal = ({ assetData, collectionData, slug }: AddToWishListModalProps) => {
    const { t: translate } = useTranslation(['nft_details']);
    const { tablet } = useMediaQuery();
    const dispatch = useAppDispatch();

    const [step, setStep] = useState<WishlistStep>('choose');
    const wishListModalSlug = useAppSelector((state) => state.wishlist.wishListModalSlug);
    const wishListModalData = useAppSelector((state) => state.wishlist.wishListModalData);

    const [popoverFullyOpen, setPopoverFullyOpen] = useState(false);

    const { data: wishlistData } = useWishlistData();

    const wishlists = getPagesContent(wishlistData?.pages);
    const activeWishlist = wishlistData ? wishlists.find((data) => wishListModalSlug === data?.slug) : undefined;

    const activeWishlistSlug = activeWishlist?.slug;

    const [active, setActive] = useState<string | undefined>(activeWishlistSlug);

    const { animation_url, image_url, collection, token_id } = assetData?.token_metadata || {};

    const { moveToWishlist, isLoading: isMoving } = useMoveToWishlist({
        contract: wishListModalData?.contract_address || collection,
        token_id: wishListModalData?.token_id || token_id,
        slug: wishListModalData?.slug || slug,
    });

    const handleStepChange = (value: WishlistStep) => () => {
        setStep(value);
    };

    const handleMove = (newWishlistSlug?: string, newWishlistName?: string) => {
        if (activeWishlistSlug && newWishlistSlug && newWishlistName) {
            setActive(newWishlistSlug);
            moveToWishlist({ oldWishlistSlug: activeWishlistSlug, newWishlistSlug, newWishlistName });
        }
    };

    const footer =
        step === 'choose' ? (
            <Footer>
                <CreateWrapper onClick={handleStepChange('create')}>
                    <AddIconCircle>
                        <PlusIcon />
                    </AddIconCircle>
                    <CreateText>{translate('detail_view_wishlist_create_wishlist')}</CreateText>
                </CreateWrapper>
            </Footer>
        ) : null;

    const wishlistItems = wishlists.filter((data) => data?.slug !== activeWishlistSlug);

    const chooseContent = (
        <ModalLayout>
            <SectionWrapper>
                {assetData || collectionData ? (
                    <NFTAssetWrapper>
                        <DepNftAsset
                            errorSquareBackground={false}
                            imageUrl={wishListModalData?.image || image_url || collectionData?.image_url}
                            videoUrl={animation_url}
                        />
                    </NFTAssetWrapper>
                ) : (
                    <StyledStar forceSprite emojiID="⭐" />
                )}
            </SectionWrapper>
            <SectionWrapper>
                <Typography size={tablet ? 40 : 30} type="headline" variant="span" align="center" weight="700">
                    {translate('detail_view_wishlist_heading')}
                </Typography>
            </SectionWrapper>

            <SectionWrapper>
                <SubheadingLabel>{translate('detail_view_wishlist_subheading_current')}</SubheadingLabel>
                {activeWishlist ? <WishlistItemCard isDefault {...activeWishlist} /> : null}
            </SectionWrapper>
            <SectionWrapper>
                {wishlistItems && wishlistItems.length > 0 ? (
                    <SubheadingLabel>{translate('detail_view_wishlist_subheading_choose')}</SubheadingLabel>
                ) : null}
                {wishlistItems?.map((itemData) => {
                    return (
                        <WishlistItemCard
                            key={itemData.id}
                            canMove={!!activeWishlistSlug}
                            isMoving={isMoving}
                            activeSlug={active}
                            onConfirm={() => handleMove(itemData.slug, itemData.name)}
                            {...itemData}
                        />
                    );
                })}
            </SectionWrapper>
        </ModalLayout>
    );

    const handleClose = () => {
        if (!popoverFullyOpen) dispatch(wishlistActions.setWishListModalSlug(null));
    };

    return (
        <AnimatePresence>
            {wishListModalSlug && (
                <Modal onClose={handleClose} footer={footer} mobileDarkBackground>
                    {step === 'choose' && chooseContent}
                    {step === 'create' && (
                        <CreateWishlist
                            onBack={() => setStep('choose')}
                            assetData={wishListModalData || assetData}
                            slug={slug}
                            thumbnail={collectionData?.image_url}
                            oldWishlistSlug={activeWishlistSlug}
                            setPopoverFullyOpen={setPopoverFullyOpen}
                        />
                    )}
                </Modal>
            )}
        </AnimatePresence>
    );
};
